import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Copy, Loader2, Mail, MessageCircle, MessageSquare, Share2, X } from "lucide-react";
import { copyTextToClipboard } from "../utils/shareInvite";

interface ShareDealSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Message without the link, e.g. "Item for $4.97 (50% off) at Walmart". */
  text: string;
  imageUrl?: string;
  /** Short price line shown under the product name in the preview. */
  priceLine?: string;
  /** Resolves the affiliate link to share; called each time the sheet opens. */
  resolveUrl: () => Promise<string>;
}

type Target = { key: string; label: string; icon: React.ReactNode; href: (msg: string, url: string) => string };

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.026 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
  </svg>
);

const TARGETS: Target[] = [
  { key: "sms", label: "Messages", icon: <MessageSquare className="h-5 w-5" />, href: (msg, url) => `sms:?&body=${encodeURIComponent(`${msg} ${url}`)}` },
  { key: "whatsapp", label: "WhatsApp", icon: <MessageCircle className="h-5 w-5" />, href: (msg, url) => `https://wa.me/?text=${encodeURIComponent(`${msg} ${url}`)}` },
  { key: "x", label: "X", icon: <XIcon />, href: (msg, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}&url=${encodeURIComponent(url)}` },
  { key: "facebook", label: "Facebook", icon: <FacebookIcon />, href: (_msg, url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { key: "email", label: "Email", icon: <Mail className="h-5 w-5" />, href: (msg, url) => `mailto:?subject=${encodeURIComponent(msg)}&body=${encodeURIComponent(`${msg}\n\n${url}`)}` },
];

export default function ShareDealSheet({ isOpen, onClose, title, text, imageUrl, priceLine, resolveUrl }: ShareDealSheetProps) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setUrl("");
    setCopied(false);
    setLoading(true);
    resolveUrl()
      .then((link) => { if (!cancelled) setUrl(link); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!url) return;
    if (await copyTextToClipboard(url)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (!url) return;
    try {
      await navigator.share({ title, text, url });
    } catch {
      // canceled or blocked; the sheet stays open with the other options
    }
  };

  const disabled = loading || !url;

  return createPortal(
    <div
      className="fixed inset-0 z-[10040] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onPointerDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Share deal"
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl sm:rounded-2xl dark:bg-dark-900"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 dark:text-white">Share deal</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-dark-800 dark:text-dark-400 dark:hover:bg-dark-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-2.5 dark:border-dark-700 dark:bg-dark-800">
          {imageUrl && (
            <img src={imageUrl} alt="" className="h-12 w-12 flex-shrink-0 rounded-lg bg-white object-contain" />
          )}
          <div className="min-w-0">
            <p className="line-clamp-2 text-xs font-medium text-gray-700 dark:text-dark-200">{title}</p>
            {priceLine && <p className="mt-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">{priceLine}</p>}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <div className="flex h-10 min-w-0 flex-1 items-center rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-600 dark:border-dark-700 dark:bg-dark-950 dark:text-dark-300">
            {loading ? (
              <span className="flex items-center gap-2 text-gray-400"><Loader2 className="h-3.5 w-3.5 animate-spin" />Getting link…</span>
            ) : url ? (
              <span className="truncate" title={url}>{url}</span>
            ) : (
              <span className="text-red-500">No link available for this product</span>
            )}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            disabled={disabled}
            className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
          {canNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              disabled={disabled}
              className="flex flex-col items-center gap-1.5 rounded-xl py-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:text-dark-200 dark:hover:bg-dark-800"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-700 dark:bg-dark-800 dark:text-dark-200">
                <Share2 className="h-5 w-5" />
              </span>
              More
            </button>
          )}
          {TARGETS.map((target) => (
            <a
              key={target.key}
              href={disabled ? undefined : target.href(text, url)}
              target={target.key === "sms" || target.key === "email" ? undefined : "_blank"}
              rel="noopener noreferrer"
              aria-disabled={disabled}
              className={`flex flex-col items-center gap-1.5 rounded-xl py-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:text-dark-200 dark:hover:bg-dark-800 ${disabled ? "pointer-events-none opacity-50" : ""}`}
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-700 dark:bg-dark-800 dark:text-dark-200">
                {target.icon}
              </span>
              {target.label}
            </a>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
